import { useMemo } from "react";

import NoData from "@/components/desktop/no-data/NoData";

import styles from "./ResponsiveCardList.module.scss";

const isEmptyValue = (value) => value == null || value === "" || value === "-";

const buildAccessor = (column) => (typeof column.accessor === "string" ? column.accessor : column.id);

const resolveCellValue = (column, rowData) => {
	if (typeof column.accessor === "function") {
		return column.accessor(rowData);
	}
	const accessor = buildAccessor(column);
	return accessor && typeof rowData === "object" ? rowData[accessor] : undefined;
};

const renderHeader = (column) => {
	if (typeof column.Header === "function") {
		return column.Header({});
	}
	return column.Header;
};

const renderCell = (column, rowData, rowIndex) => {
	const value = resolveCellValue(column, rowData);

	if (typeof column.Cell === "function") {
		const fakeRow = {
			original: rowData,
			values: rowData,
			index: rowIndex,
			id: String(rowIndex),
		};
		return column.Cell({ row: fakeRow, value, cell: { value }, column });
	}

	if (isEmptyValue(value)) {
		return <span className={styles.emptyValue}>-</span>;
	}

	return value;
};

const cellHasContent = (column, rowData) => {
	if (typeof column.Cell === "function") return true;
	return !isEmptyValue(resolveCellValue(column, rowData));
};

const splitColumnsForCard = (columns) => {
	const summaryColumns = [];
	const detailColumns = [];
	let selectColumn = null;
	let actionsColumn = null;

	columns.forEach((column) => {
		if (column.id === "select") {
			selectColumn = column;
			return;
		}
		if (column.id === "actions") {
			actionsColumn = column;
			return;
		}
		if (column.hideOnMobile === true) return;

		if (column.mobileSummary === true) {
			summaryColumns.push(column);
		} else {
			detailColumns.push(column);
		}
	});

	const hasSummary = summaryColumns.length > 0;
	const finalSummary = hasSummary ? summaryColumns : detailColumns.slice(0, 1);
	const finalDetails = hasSummary ? detailColumns : detailColumns.slice(1);

	return { selectColumn, actionsColumn, summaryColumns: finalSummary, detailColumns: finalDetails };
};

const CardItem = ({ rowData, rowIndex, selectColumn, actionsColumn, summaryColumns, detailColumns, onRowClick }) => {
	const visibleDetails = useMemo(() => detailColumns.filter((column) => cellHasContent(column, rowData)), [detailColumns, rowData]);

	const handleCardClick = (eventDom) => {
		if (eventDom.target.closest("[data-card-noclick]")) return;
		if (typeof onRowClick === "function") onRowClick(rowData);
	};

	return (
		<article className={styles.card} onClick={onRowClick ? handleCardClick : undefined} role={onRowClick ? "button" : undefined}>
			<header className={styles.cardHeader}>
				{selectColumn && (
					<div className={styles.cardSelect} data-card-noclick>
						{renderCell(selectColumn, rowData, rowIndex)}
					</div>
				)}
				<div className={styles.cardSummary}>
					{summaryColumns.map((column) => (
						<div key={column.id || buildAccessor(column)} className={styles.summaryItem}>
							{renderCell(column, rowData, rowIndex)}
						</div>
					))}
				</div>
				{actionsColumn && (
					<div className={styles.cardActions} data-card-noclick>
						{renderCell(actionsColumn, rowData, rowIndex)}
					</div>
				)}
			</header>

			{visibleDetails.length > 0 && (
				<div className={styles.cardBody}>
					{visibleDetails.map((column) => (
						<div key={column.id || buildAccessor(column)} className={styles.cardRow}>
							<div className={styles.cardLabel}>{renderHeader(column)}</div>
							<div className={styles.cardValue}>{renderCell(column, rowData, rowIndex)}</div>
						</div>
					))}
				</div>
			)}
		</article>
	);
};

const ResponsiveCardList = ({ columns = [], data = [], onRowClick }) => {
	const layout = useMemo(() => splitColumnsForCard(columns), [columns]);

	if (!Array.isArray(data) || data.length === 0) {
		return (
			<div className={styles.emptyWrapper}>
				<NoData />
			</div>
		);
	}

	return (
		<div className={styles.cardList}>
			{data.map((rowData, rowIndex) => (
				<CardItem
					key={rowData?.id || rowData?.app_order_id || rowIndex}
					rowData={rowData}
					rowIndex={rowIndex}
					selectColumn={layout.selectColumn}
					actionsColumn={layout.actionsColumn}
					summaryColumns={layout.summaryColumns}
					detailColumns={layout.detailColumns}
					onRowClick={onRowClick}
				/>
			))}
		</div>
	);
};

export default ResponsiveCardList;
